/**
 * Orden del menú (guardado en el servidor, SQLite; ver src/server/). FUENTE ÚNICA de cómo se
 * aplica: la usan el menú lateral (MenuService), el árbol de /admin/paginas y el servidor.
 *
 * Cada nivel se ordena por `permissionIdentifier`. Lo que el archivo no nombra conserva el
 * orden del backend y va después de lo nombrado: olvidar algo en el archivo nunca lo oculta.
 */

export interface MenuOrderSection {
    id: string;
    /** Identificadores de página, en el orden deseado. */
    pages?: string[];
}

export interface MenuOrderModule {
    id: string;
    sections?: MenuOrderSection[];
}

export interface MenuOrderConfig {
    modules?: MenuOrderModule[];
}

interface Orderable {
    permissionIdentifier?: string;
}

interface OrderableSection extends Orderable {
    pages?: Orderable[];
}

interface OrderableModule extends Orderable {
    sections?: OrderableSection[];
}

export const EMPTY_MENU_ORDER: MenuOrderConfig = { modules: [] };

/** `true` si `value` tiene la forma de MenuOrderConfig. El servidor lo exige antes de guardar. */
export function isMenuOrderConfig(value: unknown): value is MenuOrderConfig {
    const isIdList = (list: unknown): boolean =>
        Array.isArray(list) && list.every((item) => typeof item === 'string');
    const isSection = (section: unknown): boolean =>
        isRecord(section) && typeof section['id'] === 'string' && (section['pages'] === undefined || isIdList(section['pages']));
    const isModule = (module: unknown): boolean =>
        isRecord(module) &&
        typeof module['id'] === 'string' &&
        (module['sections'] === undefined || (Array.isArray(module['sections']) && module['sections'].every(isSection)));

    return isRecord(value) && (value['modules'] === undefined || (Array.isArray(value['modules']) && value['modules'].every(isModule)));
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function applyMenuOrder<M extends OrderableModule>(modules: M[], config: MenuOrderConfig): M[] {
    const moduleOrder = config.modules ?? [];
    if (moduleOrder.length === 0) {
        return modules;
    }

    return sortByIdentifiers(modules, moduleOrder.map((entry) => entry.id)).map((module) => {
        const moduleEntry = findEntry(moduleOrder, module.permissionIdentifier);
        if (!moduleEntry?.sections?.length || !module.sections) {
            return module;
        }

        const sectionOrder = moduleEntry.sections;
        const sections = sortByIdentifiers(module.sections, sectionOrder.map((entry) => entry.id)).map((section) => {
            const pageOrder = findEntry(sectionOrder, section.permissionIdentifier)?.pages;
            return pageOrder?.length && section.pages
                ? { ...section, pages: sortByIdentifiers(section.pages, pageOrder) }
                : section;
        });

        return { ...module, sections } as M;
    });
}

/** Orden estable: primero lo nombrado (en su orden), después el resto como vino. */
function sortByIdentifiers<T extends Orderable>(items: T[], identifiers: string[]): T[] {
    const position = new Map(identifiers.map((identifier, index) => [normalize(identifier), index]));
    return items
        .map((item, index) => ({ item, index, rank: position.get(normalize(item.permissionIdentifier)) }))
        .sort((a, b) => {
            if (a.rank !== undefined && b.rank !== undefined) return a.rank - b.rank;
            if (a.rank !== undefined) return -1;
            if (b.rank !== undefined) return 1;
            return a.index - b.index;
        })
        .map(({ item }) => item);
}

function findEntry<E extends { id: string }>(entries: E[], identifier: string | undefined): E | undefined {
    const key = normalize(identifier);
    return entries.find((entry) => normalize(entry.id) === key);
}

function normalize(identifier: string | undefined): string {
    return (identifier ?? '').trim().toLowerCase();
}
